import React, { useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { LearningMode } from 'app/types';
import { EXERCISES } from 'pages/Learning/learningMeta';
import LearningModeIcon from 'pages/Learning/components/LearningModeIcon';
import FilledActionButton from 'components/FilledActionButton';
import LearningStartPopover from 'pages/Learning/components/LearningStartPopover';

interface LearningHowItWorksProps {
  onStart: (mode: LearningMode, options?: { wordCount?: number }) => void;
  starting?: boolean;
}

const LearningHowItWorks: React.FC<LearningHowItWorksProps> = ({
  onStart,
  starting,
}) => {
  const startBtnRef = useRef<HTMLButtonElement | null>(null);
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);

  const openStartPopover = () => setStartPopoverOpen(true);
  const closeStartPopover = () => setStartPopoverOpen(false);
  const confirmStart = (wordCount: number) => {
    closeStartPopover();
    onStart('NEW_WORDS', { wordCount });
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -26px rgba(20,20,20,0.4)',
        p: { xs: 2.5, md: '34px 36px' },
      }}
    >
      <Box sx={{ maxWidth: 600, mb: { xs: 2.5, md: '26px' } }}>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.09em',
            mb: 1.25,
            textTransform: 'uppercase',
          }}
        >
          {'Вправи на вивчення'}
        </Typography>
        <Typography
          component={'h1'}
          sx={{
            fontSize: { xs: 24, md: 32 },
            fontWeight: 800,
            lineHeight: 1.15,
            mb: 1.25,
          }}
        >
          {'Вивчай слова через короткі вправи'}
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: { xs: 14, md: 17 },
            lineHeight: 1.5,
          }}
        >
          {
            'Обери набір слів, пройди кілька типів вправ і збережи результат у Мій словник. Усе займає кілька хвилин.'
          }
        </Typography>
      </Box>

      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.06em',
          mb: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {'Що буде у сесії'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          mb: { xs: 2.5, md: '28px' },
        }}
      >
        {EXERCISES.map((e, i) => (
          <Box
            key={e.id}
            sx={{
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              borderRadius: 2,
              p: '18px 16px',
            }}
          >
            <Stack direction={'row'} alignItems={'center'} spacing={1.25}>
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(59, 44, 147, 0.08)'
                      : 'rgba(155, 124, 255, 0.18)',
                  borderRadius: 1.25,
                  color: 'primary.main',
                  display: 'inline-flex',
                  flex: 'none',
                  height: 40,
                  justifyContent: 'center',
                  width: 40,
                }}
              >
                <LearningModeIcon name={e.icon} size={23} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 700,
                    mb: 0.25,
                  }}
                >
                  {`Крок ${i + 1}`}
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                  {e.label}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={0.5}
          sx={{ color: 'text.secondary', fontSize: 13 }}
        >
          <ScheduleIcon sx={{ fontSize: 16 }} />
          <Box component={'span'}>{'~8 хв на сесію'}</Box>
        </Stack>
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <Button
          variant={'outlined'}
          startIcon={<ChecklistIcon />}
          onClick={() => onStart('MANUAL')}
          disabled={starting}
          sx={{ height: 44, px: 2.5 }}
        >
          {'Обрати слова самостійно'}
        </Button>
        <FilledActionButton
          ref={startBtnRef}
          startIcon={<PlayArrowIcon />}
          onClick={openStartPopover}
          disabled={starting}
          sx={{ fontSize: 14, height: 44, px: 2.5 }}
        >
          {'Почати з нових слів'}
        </FilledActionButton>
      </Stack>

      <LearningStartPopover
        anchorEl={startBtnRef.current}
        open={startPopoverOpen}
        onClose={closeStartPopover}
        onStart={confirmStart}
        starting={starting}
      />
    </Box>
  );
};

export default LearningHowItWorks;
